import { useAtom, useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { inboxesService } from '@/services/inboxes.service';
import { selectedAccountIdAtom } from '@/hooks/use-account';
import { queryClient } from '@/lib/query-client';
import type { CreateInboxDto, UpdateInboxDto } from '@/types';

const inboxesQueryAtom = atomWithQuery((get) => {
  const id = get(selectedAccountIdAtom);
  return {
    queryKey: ['inboxes', id],
    enabled: id != null,
    queryFn: () => inboxesService.list(id as number)
  };
});

export const useInboxes = () => {
  const [result] = useAtom(inboxesQueryAtom);
  return result;
};

export const useInboxMutations = () => {
  const id = useAtomValue(selectedAccountIdAtom);

  const requireId = (): number => {
    if (id == null) throw new Error('No account selected');
    return id;
  };
  // Assigning an inbox changes which unit owns it, so the business-unit list (and its
  // inbox counts) is stale too — refresh both rather than leaving the counts wrong.
  //
  // The picker goes as well: creating an inbox is what claims a destination, so leaving
  // it cached would keep offering one that is now taken.
  const refresh = async (accountId: number) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['inboxes', accountId] }),
      queryClient.invalidateQueries({ queryKey: ['business-units', accountId] }),
      queryClient.invalidateQueries({
        queryKey: ['channel-identifiers-available', accountId]
      })
    ]);
  };

  return {
    createInbox: async (payload: CreateInboxDto) => {
      const accountId = requireId();
      const res = await inboxesService.create(accountId, payload);
      await refresh(accountId);
      return res;
    },
    updateInbox: async (inboxId: number, payload: UpdateInboxDto) => {
      const accountId = requireId();
      const res = await inboxesService.update(accountId, inboxId, payload);
      await refresh(accountId);
      return res;
    },
    deleteInbox: async (inboxId: number) => {
      const accountId = requireId();
      await inboxesService.remove(accountId, inboxId);
      await refresh(accountId);
    },
    assignBusinessUnit: async (inboxId: number, buId: number) => {
      const accountId = requireId();
      await inboxesService.assignBusinessUnit(accountId, inboxId, buId);
      await refresh(accountId);
    },
    clearBusinessUnit: async (inboxId: number) => {
      const accountId = requireId();
      await inboxesService.clearBusinessUnit(accountId, inboxId);
      await refresh(accountId);
    },
    shareWithBusinessUnit: async (inboxId: number, buId: number) => {
      const accountId = requireId();
      const res = await inboxesService.shareWithBusinessUnit(accountId, inboxId, buId);
      await refresh(accountId);
      return res;
    },
    unshareFromBusinessUnit: async (inboxId: number, buId: number) => {
      const accountId = requireId();
      const res = await inboxesService.unshareFromBusinessUnit(accountId, inboxId, buId);
      await refresh(accountId);
      return res;
    },
    setPrimaryBusinessUnit: async (inboxId: number, buId: number) => {
      const accountId = requireId();
      const res = await inboxesService.setPrimaryBusinessUnit(accountId, inboxId, buId);
      await refresh(accountId);
      return res;
    }
  };
};
